import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLinks } from '@/lib/firebase/queries/links';
import { getDonationSumsByLinkIds } from '@/lib/firebase/queries/donations';
import { useMedicalProviders } from '@/lib/firebase/queries/medicalProviders';
import { useTreatmentSupportRequests } from '@/hooks/use-treatment-support';
import {
  buildComputedLinks,
  filterComputedLinks,
  sortComputedLinks,
  linksSummary,
  pctOf,
  type ComputedLink,
} from '@/lib/linkFinancials';
import { useMarketerProjectDialogs } from '@/components/marketer/MarketerProjectDialogs';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SaudiRiyalIcon } from '@/components/ui/saudi-riyal-icon';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, Search } from 'lucide-react';

type SortColumn = 'platformCaseNumber' | 'platform' | 'actualReceived' | 'marketingShare' | 'availableForCases' | 'totalAllocated' | 'remaining' | 'refundableVAT' | 'cashback';

/**
 * Projects surface for the marketer role: read (via Task 1's pure linkFinancials module) plus
 * the mutation dialogs from MarketerProjectDialogs.tsx (add/edit/delete a project, manage its
 * donation transactions). The only case write anywhere on this surface is the donationEntries
 * strip performed by MarketerProjectDialogs' delete-project handler — everything else here is
 * read-only toward `treatmentSupportRequests`.
 */
export function MarketerProjects() {
  const { t, dir } = useLanguage();
  const { links, loading: linksLoading } = useLinks();
  const { requests, loading: requestsLoading, updateRequest } = useTreatmentSupportRequests();
  const { providers, loading: providersLoading } = useMedicalProviders();

  // actualReceived per link, fetched via server-side sum('amount') aggregation instead of
  // downloading the whole donations collection (see donations.ts getDonationSumsByLinkIds) — the
  // fix for the production hang on the mobile equivalent screen: an unbounded onSnapshot over
  // 74k+ donation docs was saturating the single long-polling Firestore stream.
  const [linkTotals, setLinkTotals] = useState<Record<string, number>>({});
  const [totalsLoading, setTotalsLoading] = useState(true);

  const [marketingX, setMarketingX] = useState(0.4);
  const [marketingY, setMarketingY] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  // Sort defaults match mobile (MarketerProjectsScreen): platformCaseNumber/asc, and clicking a
  // new column defaults to asc too (see handleSort below).
  const [sortColumn, setSortColumn] = useState<SortColumn>('platformCaseNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const loadMarketingSettings = async () => {
      try {
        const marketingDoc = await getDoc(doc(db, 'settings', 'treatmentMarketing'));
        if (marketingDoc.exists()) {
          const data = marketingDoc.data();
          if (typeof data.x === 'number') setMarketingX(data.x);
          if (typeof data.y === 'number') setMarketingY(data.y);
        }
      } catch (error) {
        console.error('Error loading marketing settings:', error);
      }
    };
    loadMarketingSettings();
  }, []);

  // Refetches every visible link's donation total. Runs whenever `links` changes (a new/removed
  // link needs its total recomputed) and is also handed down to the dialogs hook so it can call
  // this explicitly after any action that writes a donation document without also touching the
  // `links` collection (add/edit/delete a donation) — those writes don't retrigger useLinks().
  const refreshLinkTotals = useCallback(async () => {
    const ids = links.map((l) => l.id);
    if (ids.length === 0) { setLinkTotals({}); setTotalsLoading(false); return; }
    setLinkTotals(await getDonationSumsByLinkIds(ids));
    setTotalsLoading(false);
  }, [links]);

  useEffect(() => {
    refreshLinkTotals();
  }, [refreshLinkTotals]);

  const computedLinks = useMemo(
    () => buildComputedLinks(links, linkTotals, requests, providers, marketingX, marketingY),
    [links, linkTotals, requests, providers, marketingX, marketingY]
  );

  const filtered = useMemo(() => filterComputedLinks(computedLinks, searchQuery), [computedLinks, searchQuery]);
  const sorted = useMemo(() => sortComputedLinks(filtered, sortColumn, sortDirection), [filtered, sortColumn, sortDirection]);
  const summary = useMemo(() => linksSummary(filtered), [filtered]);

  const { addButtonNode, renderRowMenu, dialogsNode } = useMarketerProjectDialogs({
    links,
    requests,
    updateRequest,
    marketingX,
    marketingY,
    refreshLinkTotals,
  });

  const loading = linksLoading || totalsLoading || requestsLoading || providersLoading;

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ms-1 opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ms-1" /> : <ArrowDown className="h-3 w-3 ms-1" />;
  };

  const summaryCards: { label: string; value: number }[] = [
    { label: t('dashboard.marketer.projects.summary.totalReceived'), value: summary.totalReceived },
    { label: t('dashboard.marketer.projects.summary.totalMarketing'), value: summary.totalMarketing },
    { label: t('dashboard.marketer.projects.summary.totalNet'), value: summary.totalNet },
    { label: t('dashboard.marketer.projects.summary.totalAllocated'), value: summary.totalAllocated },
    { label: t('dashboard.marketer.projects.summary.totalRemaining'), value: summary.totalRemaining },
  ];

  const sortableHeadClass = `${dir === 'rtl' ? 'text-right' : 'text-left'} cursor-pointer select-none hover:bg-muted/50`;
  const headClass = dir === 'rtl' ? 'text-right' : 'text-left';

  return (
    <div className="space-y-4" dir={dir}>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('dashboard.marketer.projects.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ps-10"
          />
        </div>
        <div className="flex items-center justify-center sm:justify-start gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground whitespace-nowrap">
            <span className="font-semibold text-foreground">{filtered.length.toLocaleString('en-US')}</span>
            {t('dashboard.marketer.projects.totalProjects')}
          </span>
          {addButtonNode}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {summaryCards.map((c, i) => (
          <div key={i} className="flex-1 min-w-0 px-3 py-2 rounded-lg border bg-card">
            <p className="text-xs text-muted-foreground whitespace-nowrap">{c.label}</p>
            <p className="text-lg font-bold inline-flex items-center gap-1">
              {Math.round(c.value).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
            </p>
            <p className="text-xs text-muted-foreground">{pctOf(c.value, summary.totalReceived)}</p>
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t('dashboard.marketer.projects.title')}</h3>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin me-2" />
              {t('dashboard.marketer.projects.loading')}
            </div>
          ) : sorted.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t('dashboard.marketer.projects.noProjects')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className={headClass}>{t('dashboard.marketer.projects.columns.index')}</TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('platform')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.platform')}{sortIcon('platform')}</span>
                    </TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('platformCaseNumber')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.platformCaseNo')}{sortIcon('platformCaseNumber')}</span>
                    </TableHead>
                    <TableHead className={headClass}>{t('dashboard.marketer.projects.columns.linkTitle')}</TableHead>
                    <TableHead className={`${headClass} min-w-[200px]`}>{t('dashboard.marketer.projects.columns.cases')}</TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('actualReceived')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.received')}{sortIcon('actualReceived')}</span>
                    </TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('marketingShare')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.marketing')}{sortIcon('marketingShare')}</span>
                    </TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('availableForCases')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.net')}{sortIcon('availableForCases')}</span>
                    </TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('totalAllocated')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.allocated')}{sortIcon('totalAllocated')}</span>
                    </TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('remaining')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.remaining')}{sortIcon('remaining')}</span>
                    </TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('refundableVAT')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.vat')}{sortIcon('refundableVAT')}</span>
                    </TableHead>
                    <TableHead className={sortableHeadClass} onClick={() => handleSort('cashback')}>
                      <span className="inline-flex items-center">{t('dashboard.marketer.projects.columns.cashback')}{sortIcon('cashback')}</span>
                    </TableHead>
                    <TableHead className={headClass}></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((l: ComputedLink, idx: number) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="text-sm">{t(`dashboard.coordinator.platforms.${l.platform}`)}</TableCell>
                      <TableCell className="font-mono text-xs" dir="ltr">{l.platformCaseNumber}</TableCell>
                      <TableCell className="text-sm">{l.linkTitle || '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1.5">
                          {l.cases.length === 0 ? (
                            <span className="text-muted-foreground text-xs">{t('dashboard.marketer.projects.noCases')}</span>
                          ) : (
                            l.cases.map((c, ci) => (
                              <span
                                key={ci}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs font-medium border"
                                title={`#${c.caseNumber ?? '-'} · ${c.idNumber || '-'} · ${(c.allocated || 0).toLocaleString('en-US')} SAR`}
                              >
                                <span className="font-semibold">{dir === 'rtl' ? c.nameAr : c.nameEn}</span>
                                {c.idNumber && <span className="text-muted-foreground font-mono" dir="ltr">{c.idNumber}</span>}
                              </span>
                            ))
                          )}
                        </div>
                      </TableCell>
                      {/* Rounded for display to match mobile's formatCost (Math.round) — a fractional
                          donation must not show a different figure on web vs mobile. */}
                      <TableCell>{Math.round(l.actualReceived).toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-muted-foreground">{Math.round(l.marketingShare).toLocaleString('en-US')}</TableCell>
                      <TableCell>{Math.round(l.availableForCases).toLocaleString('en-US')}</TableCell>
                      <TableCell>{Math.round(l.totalAllocated).toLocaleString('en-US')}</TableCell>
                      <TableCell className="font-medium">{Math.round(l.remaining).toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-muted-foreground">{Math.round(l.refundableVAT).toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-muted-foreground">{Math.round(l.cashbackAmount).toLocaleString('en-US')}</TableCell>
                      <TableCell>{renderRowMenu(l)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {dialogsNode}
    </div>
  );
}
